// "Help keep SpotDrop free" — small independent-support section on the
// landing. Always renders (with heart title, per current growth-batch spec).
// The button destination comes from REACT_APP_REVOLUT_SUPPORT_URL.
// While the env var is empty, the button is disabled and shows "Coming soon"
// so users see the intent but can't click through. Set the env var to enable.
//
// SpotDrop must NOT process payments or store payment info. Tapping the
// button just opens the configured URL in a new tab.

import React from "react";
import { motion } from "framer-motion";
import { Heart, ExternalLink } from "lucide-react";

const REVOLUT_URL = (process.env.REACT_APP_REVOLUT_SUPPORT_URL || "").trim();

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export default function SupportSection() {
  // When no support URL is configured, hide the section entirely — do NOT
  // show "Coming soon" to public visitors. Configure REACT_APP_REVOLUT_SUPPORT_URL
  // to reveal the section.
  if (!REVOLUT_URL) return null;

  return (
    <section
      id="support"
      className="relative py-16 md:py-20 border-t border-white/5"
      data-testid="support-section"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="p-6 md:p-8 rounded-2xl bg-[#141416] border border-white/10 flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-semibold flex items-center gap-2">
              <Heart className="w-3 h-3 text-[#ccff00]" />
              Help keep SpotDrop free
            </div>
            <div className="mt-2 text-sm md:text-base text-zinc-300 leading-relaxed">
              SpotDrop is built independently for drivers in Malta. If it has helped
              you, you can support hosting, development and future features.
            </div>
          </div>
          {(
            <a
              href={REVOLUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center justify-center gap-2 h-11 rounded-full px-5 bg-white/[0.04] border border-white/15 text-white hover:bg-white/[0.08] text-sm font-medium transition-colors"
              data-testid="support-revolut-btn"
            >
              Support SpotDrop
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </motion.div>
      </div>
    </section>
  );
}
